import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';

import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import {analyticsClient} from '../../../../analytics-client';
import {
    readLocalStorage,
    removeLocalStorageItem,
    writeLocalStorage,
} from '../../../../util/localStorage';

import styles from './success-state.module.css';

type Props = {
    isTrial: boolean;
};

export function SuccessState(props: Props) {
    const [needsRefresh, setNeedsRefresh] = React.useState(true);
    const isTrial = props.isTrial;

    // Hard refresh the page when we hit the success page, to make sure the
    // user sees the proper plan. This hook ensures we hit the success
    // page after the refresh, rather than being set back to the start of the
    // manage flow.
    React.useEffect(() => {
        if (readLocalStorage('billingSuccess')) {
            removeLocalStorageItem('billingSuccess');
            setNeedsRefresh(false);
        } else {
            const writeSucceeded = writeLocalStorage('billingSuccess', 'true');
            if (writeSucceeded) {
                window.location.reload();
            } else {
                // unable to write, skip refresh to avoid loop
                setNeedsRefresh(false);
            }
        }
    }, []);

    React.useEffect(() => {
        if (isTrial) {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_VISITS_BILLING_SUCCESS
            );
        }
    }, [isTrial]);

    if (needsRefresh) {
        return null;
    }

    return (
        <div
            className={classnames(
                'flex gap-48 pad-32 justify-center align-center',
                styles.container
            )}
        >
            <div className={styles.illustration}>
                <Illustration src='high-five' width={250} />
            </div>
            <div className='flex flex-dir-col py-24 gap-24 mt-16'>
                <StackLegacy spacing={16}>
                    <h1 className={styles.title}>Thank you for your purchase!</h1>
                    <Body>
                        We’re thrilled to see you’re enjoying Nutshell and taking the next step by
                        adding a plan along with our awesome add-ons.
                    </Body>
                </StackLegacy>
                <StackLegacy spacing={8}>
                    <Body size='large' isBold={true}>
                        What’s next?
                    </Body>
                    <BulletedList
                        bullets={[
                            <Body key={'bullet-1'}>
                                Explore your new features by diving into the enhanced capabilities
                                of your CRM to discover how your new add-ons can boost productivity
                                and streamline your workflows
                            </Body>,
                            <Body key={'bullet-2'}>
                                <Link href='#' variant='primary'>
                                    Utilize our customer support
                                </Link>{' '}
                                to receive help and ask questions about any of the new features you
                                unlocked with your plan.
                            </Body>,
                        ]}
                        marginBottom={0}
                    />
                </StackLegacy>
                <div className='width-fit-content'>
                    {/* @ts-expect-error */}
                    <Link as={RoutedLink} to='/setup/billing' variant='button-primary' size='big'>
                        Back to billing home
                    </Link>
                </div>
            </div>
        </div>
    );
}
